import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Text,
  useToast,
  HStack,
  Checkbox,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  Divider
} from '@chakra-ui/react';
import { ContentModerationService } from '../services/moderationService';
import { useAuth } from '../contexts/AuthContext';

interface DMCARequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentId: string;
  contentTitle: string;
  contentCreatorId: string;
  contentCreatorName: string;
}

const DMCARequestModal: React.FC<DMCARequestModalProps> = ({
  isOpen,
  onClose,
  contentId,
  contentTitle,
  contentCreatorId,
  contentCreatorName
}) => {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    claimantName: '',
    claimantEmail: '',
    claimantPhone: '',
    claimantAddress: '',
    copyrightWork: '',
    originalWorkUrl: '',
    description: '',
    goodFaithStatement: false,
    perjuryStatement: false,
    signature: ''
  });

  const handleSubmit = async () => {
    if (!user || !userProfile) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to submit a DMCA request.',
        status: 'error'
      });
      return;
    }

    // Validate required fields
    const requiredFields = [
      'claimantName', 'claimantEmail', 'claimantAddress', 
      'copyrightWork', 'description', 'signature'
    ];
    
    for (const field of requiredFields) {
      if (!formData[field as keyof typeof formData]) {
        toast({
          title: 'Missing required information',
          description: `Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`,
          status: 'error'
        });
        return;
      }
    }

    if (!formData.goodFaithStatement || !formData.perjuryStatement) {
      toast({
        title: 'Legal statements required',
        description: 'You must agree to both legal statements to submit a DMCA request.',
        status: 'error'
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      await ContentModerationService.submitDMCARequest(
        formData.claimantName,
        formData.claimantEmail,
        formData.claimantPhone || undefined,
        formData.claimantAddress,
        contentId,
        contentTitle,
        contentCreatorId,
        contentCreatorName,
        formData.copyrightWork,
        formData.originalWorkUrl || undefined,
        formData.description,
        formData.goodFaithStatement,
        formData.perjuryStatement,
        formData.signature
      );

      toast({
        title: 'DMCA request submitted successfully',
        description: 'Your request has been received and will be processed according to our DMCA policy.',
        status: 'success',
        duration: 5000
      });

      // Reset form
      setFormData({
        claimantName: '',
        claimantEmail: '',
        claimantPhone: '',
        claimantAddress: '',
        copyrightWork: '',
        originalWorkUrl: '',
        description: '',
        goodFaithStatement: false,
        perjuryStatement: false,
        signature: ''
      });
      onClose();
    } catch (error: any) {
      console.error('Error submitting DMCA request:', error);
      toast({
        title: 'Failed to submit DMCA request',
        description: error.message || 'An error occurred. Please try again.',
        status: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white" maxH="90vh" overflowY="auto">
        <ModalHeader>DMCA Takedown Request</ModalHeader>
        <ModalBody>
          <VStack gap={6} align="stretch">
            {/* Content Info */}
            <Box p={4} bg="gray.700" borderRadius="md">
              <Text fontSize="sm" color="gray.400">Content being reported:</Text>
              <Text fontWeight="bold">{contentTitle}</Text>
              <Text fontSize="sm" color="gray.400">by {contentCreatorName}</Text>
            </Box>

            {/* Legal Notice */}
            <Alert status="warning" bg="yellow.900" color="yellow.100">
              <AlertIcon />
              <AlertDescription>
                <Text fontSize="sm">
                  <strong>Legal Notice:</strong> DMCA takedown requests are serious legal documents. 
                  False claims may result in legal liability. Please ensure you have the right to 
                  request removal of this content.
                </Text>
              </AlertDescription>
            </Alert>

            {/* Claimant Information */}
            <VStack gap={4} align="stretch">
              <Text fontWeight="bold" fontSize="lg">Claimant Information</Text>
              
              <HStack gap={4}>
                <FormControl isRequired>
                  <FormLabel>Full Name</FormLabel>
                  <Input
                    placeholder="Your full legal name"
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                    value={formData.claimantName}
                    onChange={(e) => setFormData(prev => ({ ...prev, claimantName: e.target.value }))}
                  />
                </FormControl>
                
                <FormControl isRequired>
                  <FormLabel>Email</FormLabel>
                  <Input
                    type="email"
                    placeholder="your.email@example.com"
                    bg="gray.700"
                    border="none"
                    color="white"
                    _placeholder={{ color: 'gray.400' }}
                    _focus={{ bg: 'gray.600' }}
                    value={formData.claimantEmail}
                    onChange={(e) => setFormData(prev => ({ ...prev, claimantEmail: e.target.value }))}
                  />
                </FormControl>
              </HStack>

              <FormControl>
                <FormLabel>Phone Number (Optional)</FormLabel>
                <Input
                  type="tel"
                  placeholder="+1 (555) 123-4567"
                  bg="gray.700"
                  border="none"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ bg: 'gray.600' }}
                  value={formData.claimantPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, claimantPhone: e.target.value }))}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Mailing Address</FormLabel>
                <Textarea
                  placeholder="Your complete mailing address"
                  bg="gray.700"
                  border="none"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ bg: 'gray.600' }}
                  value={formData.claimantAddress}
                  onChange={(e) => setFormData(prev => ({ ...prev, claimantAddress: e.target.value }))}
                  rows={3}
                />
              </FormControl>
            </VStack>

            <Divider />

            {/* Copyright Information */}
            <VStack gap={4} align="stretch">
              <Text fontWeight="bold" fontSize="lg">Copyright Information</Text>
              
              <FormControl isRequired>
                <FormLabel>Copyrighted Work</FormLabel>
                <Input
                  placeholder="Title of the copyrighted work"
                  bg="gray.700"
                  border="none"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ bg: 'gray.600' }}
                  value={formData.copyrightWork}
                  onChange={(e) => setFormData(prev => ({ ...prev, copyrightWork: e.target.value }))}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Original Work URL (Optional)</FormLabel>
                <Input
                  type="url"
                  placeholder="https://example.com/original-work"
                  bg="gray.700"
                  border="none"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ bg: 'gray.600' }}
                  value={formData.originalWorkUrl}
                  onChange={(e) => setFormData(prev => ({ ...prev, originalWorkUrl: e.target.value }))}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Description of Infringement</FormLabel>
                <Textarea
                  placeholder="Describe how the content infringes on your copyright..."
                  bg="gray.700"
                  border="none"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ bg: 'gray.600' }}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                />
              </FormControl>
            </VStack>

            <Divider />

            {/* Legal Statements */}
            <VStack gap={4} align="stretch">
              <Text fontWeight="bold" fontSize="lg">Legal Statements</Text>
              
              <FormControl isRequired>
                <Checkbox
                  isChecked={formData.goodFaithStatement}
                  onChange={(e) => setFormData(prev => ({ ...prev, goodFaithStatement: e.target.checked }))}
                >
                  <Text fontSize="sm">
                    I have a good faith belief that the use of the material in the manner complained of 
                    is not authorized by the copyright owner, its agent, or the law.
                  </Text>
                </Checkbox>
              </FormControl>

              <FormControl isRequired>
                <Checkbox
                  isChecked={formData.perjuryStatement}
                  onChange={(e) => setFormData(prev => ({ ...prev, perjuryStatement: e.target.checked }))}
                >
                  <Text fontSize="sm">
                    The information in this notification is accurate, and under penalty of perjury, 
                    I am authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.
                  </Text>
                </Checkbox>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Digital Signature</FormLabel>
                <Input
                  placeholder="Type your full name as digital signature"
                  bg="gray.700"
                  border="none"
                  color="white"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{ bg: 'gray.600' }}
                  value={formData.signature}
                  onChange={(e) => setFormData(prev => ({ ...prev, signature: e.target.value }))}
                />
              </FormControl>
            </VStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Submitting Request"
            isDisabled={
              !formData.claimantName || 
              !formData.claimantEmail || 
              !formData.claimantAddress ||
              !formData.copyrightWork ||
              !formData.description ||
              !formData.signature ||
              !formData.goodFaithStatement ||
              !formData.perjuryStatement
            }
          >
            Submit DMCA Request
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DMCARequestModal;
